import React, { useState, useEffect } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { cutTooLong } from '../../../helpers/helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'
import {
  createPropertyManager,
  deletePropertyManager,
  updatePropertyManager,
  selectPropertyManagerById,
} from '../../../reducers/Organisation/propertyManagersReducer'
import { formValidation } from '../../FormLegacy/validation'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import DeleteButton from '../../DeleteButton/DeleteButton'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import Filter from '../../Filter/Filter'
import { selectBuildingsForFilter } from '../../../reducers/buildingSelectorReducer'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

const PropertyManagerForm = () => {
  const { t } = useTranslation(['organisation', 'common'])
  const [deletePopup, setDeletePopup] = useState(false)
  const [formError, setFormError] = useState('')
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { propertyManagerId } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()

  const currentPM = useSelector((state) => selectPropertyManagerById(state)(propertyManagerId))
  const buildings = useSelector(selectBuildingsForFilter)

  const isNewMode = () => propertyManagerId === 'new'

  const handleDelete = async () => {
    try {
      await dispatch(deletePropertyManager(currentPM.id))
      displaySnackbar(t('organisation:propertyManagers.form.toasts.deleted'))
      setFormError('')
    } catch {
      setFormError(t('organisation:propertyManagers.form.serverError'))
    } finally {
      setDeletePopup(false)
    }
  }

  const getSchema = () => {
    const schema = {
      firstName: formValidation.required,
      lastName: formValidation.required,
      email: formValidation.email,
    }
    if (isNewMode()) {
      schema.password = formValidation.required
    }
    return schema
  }

  const validationSchema = Yup.object().shape(getSchema())

  const setManagerIdToParams = (propertyManager) => {
    if (propertyManager && propertyManager.id && !propertyManagerId) {
      const path =
        location.pathname[location.pathname.length - 1] !== '/'
          ? location.pathname + '/'
          : location.pathname
      navigate(path + propertyManager.id)
    }
  }

  const handleFormSubmit = async (
    { id, avatar, firstName, lastName, email, buildingIds, password },
    { setSubmitting, resetForm },
  ) => {
    const dataToSend = {
      firstName,
      lastName,
      email,
      buildingIds: buildingIds.map((x) => x.id),
    }

    if (avatar.file) {
      dataToSend.avatar = avatar.file
    }
    if (isNewMode()) {
      dataToSend.password = password
    }

    setFormError('')

    try {
      if (isNewMode()) {
        const res = await dispatch(createPropertyManager(dataToSend))
        setSubmitting(false)
        if (res.errCode) {
          setFormError(res.message)
        } else {
          resetForm()
          displaySnackbar(t('organisation:propertyManagers.form.toasts.created'))
          setFormError('')
        }
      } else {
        const res = await dispatch(updatePropertyManager(dataToSend, id))
        setSubmitting(false)
        if (res.errCode) {
          setFormError(res.message)
        } else {
          displaySnackbar(t('organisation:propertyManagers.form.toasts.updated'))
          setFormError('')
        }
      }
    } catch (err) {
      console.error('err', err)
      setSubmitting(false)
      if (err?.errCode === 409) {
        setFormError(t('organisation:shared.errors.emailExists'))
      } else {
        setFormError(err?.message || t('organisation:shared.serverError'))
      }
    }
  }

  useEffect(() => {
    if (currentPM.user.role === 'PropertyManager' && propertyManagerId !== 'new') {
      setManagerIdToParams(currentPM)
    }
  }, [currentPM, propertyManagerId])

  if (typeof currentPM !== 'undefined') {
    return (
      <React.Fragment>
        <Formik
          enableReinitialize={true}
          initialValues={{
            id: currentPM.id || '',
            avatar: {
              file: false,
              image: getAssetLink({ path: 'chitown-avatar/avatar', file: currentPM.user.avatar }),
            },
            firstName: currentPM.user.firstName || '',
            lastName: currentPM.user.lastName || '',
            email: currentPM.user.email || '',
            password: '',
            buildingIds: currentPM.buildingIds ? currentPM.buildingIds : [],
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            handleChange,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldTouched,
            isSubmitting,
            dirty,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="ctp-manager">
                <div className="ctp-manager__header">
                  <h2>
                    {isNewMode()
                      ? t('organisation:propertyManagers.form.titleNew')
                      : t('organisation:propertyManagers.form.titleEdit')}
                  </h2>
                </div>
                <div className="form form--object-profile">
                  <div className="column">
                    <div className="columns">
                      <div className="column">
                        <ImageField
                          name="avatar"
                          label={t('organisation:shared.fields.avatar.label')}
                          placeholderImage={getAssetLink({
                            path: 'chitown-avatar/avatar',
                            file: 'avatar.png',
                          })}
                          imageRatio={1}
                          value={values.avatar}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column">
                        <TextField
                          name="firstName"
                          label={t('organisation:shared.fields.firstName.label')}
                          placeholder={t('organisation:shared.fields.firstName.placeholder')}
                          value={values.firstName}
                          onChange={handleChange}
                          isError={touched.firstName && !!errors.firstName}
                          onBlur={() => setFieldTouched('firstName')}
                        />
                      </div>
                      <div className="column">
                        <TextField
                          name="lastName"
                          label={t('organisation:shared.fields.lastName.label')}
                          placeholder={t('organisation:shared.fields.lastName.placeholder')}
                          value={values.lastName}
                          onChange={handleChange}
                          isError={touched.lastName && !!errors.lastName}
                          onBlur={() => setFieldTouched('lastName')}
                        />
                      </div>
                    </div>
                    <TextField
                      name="email"
                      label={t('organisation:shared.fields.email.label')}
                      placeholder={t('organisation:shared.fields.email.placeholder')}
                      value={values.email}
                      onChange={handleChange}
                      isError={touched.email && !!errors.email}
                      errorLabel={touched.email && errors.email}
                      onBlur={() => setFieldTouched('email')}
                      disabled={!isNewMode()}
                    />
                    <div className="columns">
                      {isNewMode() && (
                        <div className="column">
                          <TextField
                            name="password"
                            label={t('organisation:propertyManagers.form.password.label')}
                            placeholder={t(
                              'organisation:propertyManagers.form.password.placeholder',
                            )}
                            value={values.password}
                            onChange={handleChange}
                            isError={touched.password && !!errors.password}
                            onBlur={() => setFieldTouched('password')}
                          />
                        </div>
                      )}
                      <div className="column">
                        <label>{t('organisation:propertyManagers.form.buildingLabel')}</label>
                        <Filter
                          name="buildingIds"
                          placeholder={cutTooLong(
                            values.buildingIds && values.buildingIds.length
                              ? values.buildingIds.map((x) => (x.name ? x.name : x.text)).join(', ')
                              : t('organisation:propertyManagers.form.buildingSelectPlaceholder'),
                            35,
                          )}
                          options={[
                            {
                              id: 0,
                              text: t('organisation:propertyManagers.list.allBuildingsOption'),
                              name: t('organisation:propertyManagers.list.allBuildingsOption'),
                              selectAll: true,
                            },
                            ...buildings,
                          ]}
                          disabled={!buildings || buildings.length <= 1}
                          value={values.buildingIds}
                          additionalClass=""
                          variant="rounded"
                          config={{ multiple: true, hasSearch: true }}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="columns form__actions">
                    <div className="column">
                      <button
                        type="submit"
                        className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                        disabled={
                          !dirty ||
                          isSubmitting ||
                          (isNewMode() && (!values.buildingIds.length || !!errors.password))
                        }
                      >
                        {t('common:actions.save')}
                      </button>
                    </div>
                    {!isNewMode() && (
                      <div className="column">
                        <DeleteButton
                          text={t('common:actions.delete')}
                          pending={isSubmitting}
                          callback={() => setDeletePopup(true)}
                        />
                      </div>
                    )}
                    <label className="help is-danger">{formError}</label>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
        <ConfirmPopup
          title={t('organisation:propertyManagers.form.deleteConfirm.title')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('organisation:propertyManagers.form.deleteConfirm.confirmText')}
          isOpen={deletePopup}
          cancelCallback={() => setDeletePopup(false)}
          confirmCallback={handleDelete}
        />
      </React.Fragment>
    )
  }

  return <p className="has-text-centered">{t('organisation:propertyManagers.form.notFound')}</p>
}

export default PropertyManagerForm
